import React from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';
import Link from '@material-ui/core/Link';

import { paramConverter } from 'modules/common';
import { getFormat, makeGraph } from 'lib/commonLib';
import { audienceGraphData } from 'lib/api/datacenter';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import ContentsWrap from 'components/molecules/ContentsWrap';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const graphList = [
  {
    title: i18next.t('총노출'),
    data: { bar: ['TOT_EPRS_CNT'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { TOT_EPRS_CNT: true },
    },
  },
  {
    title: i18next.t('지면노출'),
    data: { bar: ['PAR_EPRS_CNT'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { PAR_EPRS_CNT: true },
      right: { ADC_CF_NEW_USER_CNT: true },
    },
  },
  {
    title: i18next.t('누적유저수'),
    data: { bar: ['ADC_CF_USER_CNT'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { ADC_CF_USER_CNT: true },
    },
  },
  {
    title: i18next.t('방문유저수'),
    data: { bar: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: {},
    },
  },
  {
    title: i18next.t('클릭률'),
    data: { bar: ['CLICK_RATE'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { CLICK_RATE: true, isPercent: true },
    },
    customizeData: {
      CLICK_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
      },
    },
  },
  {
    title: i18next.t('전환율'),
    data: { bar: ['CONV_RATE'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { CONV_RATE: true, isPercent: true },
    },
    customizeData: {
      CONV_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
      },
    },
  },
  {
    title: i18next.t('반송률'),
    data: { bar: ['RETRN_RATE'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { RETRN_RATE: true, isPercent: true },
    },
    customizeData: {
      RETRN_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
      },
    },
  },
  {
    title: 'ECPM',
    data: { bar: ['ECPM'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { ECPM: true },
    },
  },
  {
    title: 'ROAS',
    data: { bar: ['ROAS'], line: ['ADC_CF_NEW_USER_CNT'] },
    yAxisData: {
      left: { ADC_CF_NEW_USER_CNT: true },
      right: { ROAS: true, isPercent: true },
    },
    customizeData: {
      ROAS: {
        isPercent: true,
      },
    },
  },
];

class AudienceGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
      noData: false,
    };
  }

  getData = () => {
    const { params, locale, t } = this.props;
    const { sDate, eDate } = params;
    const format = getFormat(locale);

    this.setState({
      ...this.state,
      loading: true,
      error: false,
      noData: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });

    audienceGraphData({
      ...paramConverter(params),
      uri: '/report/datacenter/audience',
    })
      .then((response) => {
        const { data } = response.data;
        const stringData = JSON.stringify(data);
        if (stringData !== '[]') {
          this.setState({
            ...this.state,
            primitiveData: data,
            loading: false,
            error: false,
            noData: false,
          });
          this.handleChangeGraph(graphList[0], 0)();
        } else if (stringData === '[]') {
          this.setState({
            ...this.state,
            loading: false,
            error: false,
            noData: true,
          });
        }
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
          noData: false,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 월 일 or 일 반환
  makeLabel = (onlyDay) => (originalDate) => {
    return originalDate;
  };

  xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;

    const dayStr = this.makeLabel(true)(value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={-8} height="100" dy={20} textAnchor="middle">
          {dayStr}
        </text>
      </g>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   */
  handleChangeGraph =
    (
      { title, data, yAxisData, customizeData: customize, isPercent = false },
      index
    ) =>
    (e) => {
      const { primitiveData } = this.state;

      if (typeof data !== 'undefined') {
        const { graphData, dataKeys, customizeData, keyLabel, stackId } =
          makeGraph({ data, customize, primitiveData });
        const result = (
          <MultiplexChart
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            data={graphData}
            dataKeys={dataKeys}
            title={title}
            key={title}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={stackId.length !== 0 ? stackId.join('$') : null}
            yAxisData={yAxisData}
            legend
            isPercent={isPercent}
          />
        );

        this.setState({
          ...this.state,
          curGraphComponent: result,
          selectedIdx: index,
        });
      }
    };

  render() {
    const { handleRefesh, handleChangeGraph } = this;
    const { t, type } = this.props;
    const {
      infoMessage,
      selectedIdx,
      primitiveData,
      curGraphComponent,
      loading,
      error,
      noData,
    } = this.state;

    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('오디언스 통계 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
            {type === 'main' ? (
              <div style={{ paddingLeft: 10 }}>
                <Link href="/report/datacenter/audience">+ 더보기</Link>
              </div>
            ) : null}
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || noData || error ? (
          <CurrentStatus
            type={loading ? 'loading' : noData ? 'noData' : error && 'error'}
          />
        ) : (
          <>
            <ChartButtonWrap>
              {graphList.map((item, index) => {
                return (
                  <OutlinedButton
                    key={item.title}
                    onClick={handleChangeGraph(item, index)}
                    selected={selectedIdx === index}
                    disabled={!Boolean(primitiveData)}
                  >
                    {item.title}
                  </OutlinedButton>
                );
              })}
            </ChartButtonWrap>
            <ContentsWrap contentsCount={1}>{curGraphComponent}</ContentsWrap>
          </>
        )}
      </SwitchingArea>
    );
  }
}

export default withRouter(
  withTranslation()(
    connect(
      ({ locale }) => ({
        locale: locale.locale,
      }),
      null
    )(withTheme(AudienceGraph))
  )
);
